import { useState, useEffect, useRef } from 'react';

/**
 * Hook migliorato per rilevare cambiamenti nella visibilità della pagina
 * con controlli antirimbalzo (debounce) e limitazione degli eventi
 * @param {Function} onVisible - Callback da eseguire quando la pagina diventa visibile
 * @param {Function} onHidden - Callback da eseguire quando la pagina diventa nascosta
 * @returns {boolean} Lo stato di visibilità attuale della pagina
 */
export function useVisibilityChange(
  onVisible?: () => void,
  onHidden?: () => void,
  options?: {
    // Determina se la callback onVisible viene chiamata con un ritardo
    // utile per dare priorità al rendering dell'interfaccia
    delayCallbackWhenVisible?: boolean,
    delayMs?: number,
    // Tempo minimo (ms) che deve passare tra una chiamata della callback e l'altra
    // per evitare attivazioni ripetute su cambi rapidi (default 2000ms)
    throttleMs?: number,
    // Disabilita automaticamente gli eventi per le finestre ridotte (dev tools)
    disableForSmallViewport?: boolean,
    // Larghezza minima della viewport prima di disabilitare gli eventi
    minViewportWidth?: number
  }
): boolean {
  const [isVisible, setIsVisible] = useState(!document.hidden);

  // Usa ref per evitare che l'hook si riattivi quando le callback cambiano
  const callbacksRef = useRef({ onVisible, onHidden });

  // Salva anche le options in un ref
  const optionsRef = useRef(options);

  // Ref per memorizzare l'ultimo timestamp di esecuzione della callback
  const lastCallbackTimeRef = useRef(0);

  // Ref per il timer di debounce
  const timerRef = useRef<number | null>(null);

  // Aggiorna i riferimenti quando cambiano le callback o le options
  useEffect(() => {
    callbacksRef.current = { onVisible, onHidden };
    optionsRef.current = options;
  }, [onVisible, onHidden, options]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      // Usa un flag per verificare se è cambiato lo stato effettivo di visibilità
      const wasVisible = isVisible;
      const nowVisible = !document.hidden;

      // Verifica se la viewport è troppo piccola (probabilmente dev tools aperti)
      const minWidth = optionsRef.current?.minViewportWidth || 1200;
      const disableForSmall = optionsRef.current?.disableForSmallViewport ?? true;

      if (disableForSmall && window.innerWidth < minWidth) {
        // Se la viewport è piccola, aggiorniamo lo stato ma non eseguiamo callback
        // per evitare i problemi con i dev tools
        if (wasVisible !== nowVisible) {
          setIsVisible(nowVisible);
          // console.log('[VisibilityChange] Evento ignorato per viewport ridotta:', window.innerWidth);
        }
        return;
      }

      // Aggiorna lo stato solo se è cambiato
      if (wasVisible !== nowVisible) {
        setIsVisible(nowVisible);

        // Limita la frequenza delle callback (throttling)
        const now = Date.now();
        const throttleMs = optionsRef.current?.throttleMs || 2000;
        const timeSinceLastCall = now - lastCallbackTimeRef.current;

        // Annulla eventuali timer pendenti
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }

        // Usa i ref per le callback per evitare loop di dipendenze
        if (nowVisible) {
          // Verifica se è passato abbastanza tempo dall'ultima chiamata
          if (timeSinceLastCall < throttleMs) {
            console.log('[VisibilityChange] Evento throttled, ultima chiamata troppo recente');
            return;
          }

          // Se è richiesto un ritardo, usiamo setTimeout per dare priorità al rendering UI
          if (optionsRef.current?.delayCallbackWhenVisible) {
            const delayMs = optionsRef.current?.delayMs || 500;
            timerRef.current = window.setTimeout(() => {
              callbacksRef.current.onVisible?.();
              lastCallbackTimeRef.current = Date.now();
              timerRef.current = null;
            }, delayMs);
          } else {
            callbacksRef.current.onVisible?.();
            lastCallbackTimeRef.current = Date.now();
          }
        } else {
          // Per onHidden applichiamo solo il throttling
          if (timeSinceLastCall < throttleMs) {
            console.log('[VisibilityChange] Evento onHidden throttled, ultima chiamata troppo recente');
            return;
          }

          callbacksRef.current.onHidden?.();
          lastCallbackTimeRef.current = Date.now();
        }
      }
    };

    // Aggiungi listener per i cambi di visibilità
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Pulizia
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Cancella eventuali timer pendenti
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isVisible]); // Dipendenza da isVisible per evitare render multipli

  return isVisible;
}